/**
 * What: this is a component that tracks promotions
 * Why: simplify promotion tracking
 * How: over all we use same data for impressions and interactions so why not use one component for it
 *      1. wrap the element we want to track with promotion component
 *          - promotion component should additionally wrap the element that captures interactions and handles them
 *      2. if the element is visible trigger tracking event
 *
 * End goal -> wrap the element that need to be tracked for promotion, provide props and thats that, rest is handled in tracking package
 *
 * Also same idea for product interactions
 */
import {
  TrackingPromotionCreative,
  trackPromotionInteraction,
  TrackingRecoElement,
  TrackingPageTypeWithId,
  TrackingPageTypeWithoutId,
} from '@aboutyou/legacy-tracking-sdk';
import React, {FC, PropsWithChildren} from 'react';
import {getPromotionData} from '../utils/promotionDataGetter';
import {useTrackingFactory} from '../utils/useTrackingFactory';
import {Story} from '@aboutyou/grpc-clients/tadarida/aysa_api/entities/stories/v1/story.types';
import {Outfit} from '@aboutyou/grpc-clients/tadarida/aysa_api/entities/outfits/v1/outfit.types';
import {Teaser} from '@aboutyou/grpc-clients/tadarida/aysa_api/features/ui/v1/teaser.types';
import {GetTopBannerResponse_TopBannerResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/campaigns/v1/service.types';
import {useTracking} from '../utils/utils';
import {Brand} from '../utils/types';
import {HighlightSlide} from '@aboutyou/grpc-clients/tadarida/aysa_api/features/ui/v1/highlight.types';
import {BaseTracker, BaseTrackerProps} from './BaseTracker';
import {MarkOptional} from 'ts-essentials';
import {PromotionTracker} from '@aboutyou/grpc-clients/tadarida/aysa_api/features/tracker/v1/tracker.types';

export type Promotion =
  | PromotionAdvertisement
  | BannerPromotion
  | BasOspSubscriptionTilePromotion
  | BasSovendusTilePromotion
  | BrandRecoPromotion
  | OutfitRecoPromotion
  | StoryRecoPromotion
  | TeaserRecoPromotion
  | TopBannerRecoPromotion
  | BasPromotionMonitoring
  | HighlightPromotion
  | CustomPagesPromotion
  | NewsCardPromotion
  | BasFlyoutTeaserPromotion
  | PromotionNotificationCenterCards
  | BrandOutfitTeaserRecoPromotion
  | APIPromotion;

export type PromotionAdvertisementSource =
  | TrackingPromotionCreative.BasAdpBanner
  | TrackingPromotionCreative.BasBasketBanner
  | TrackingPromotionCreative.BasBrowseTopBanner
  | TrackingPromotionCreative.BasCategoryHeader
  | TrackingPromotionCreative.BasCategoryTeaser
  | TrackingPromotionCreative.BasLogoIntegration
  | TrackingPromotionCreative.BasSearch
  | TrackingPromotionCreative.BasWishlistBanner
  | TrackingPromotionCreative.Link
  | TrackingPromotionCreative.BasAddToBasketFlyOut;

export interface PromotionNotificationCenterCards {
  source: TrackingPromotionCreative.NotificationCenterCards;
  notificationCenterCards: {
    id: number | string;
    name: string;
    path: string;
    locationId: string;
    creative: string;
    destination: string;
  };
}

export interface PromotionAdvertisement {
  source: PromotionAdvertisementSource;
  advertisement: {
    id: number | string;
    name: string;
    path: string;
    promotionId?: number | string;
  };
}

export interface BasFlyoutTeaserPromotion {
  source: TrackingPromotionCreative.BasFlyoutTeaser;
  advertisement: {
    id: number | string;
    name: string;
    path: string;
    promotionId?: number | string;
    creativeName?: string;
    locationId?: string;
  };
}

export interface TeaserRecoPromotion {
  source: TrackingPromotionCreative.Teaser;
  teaser: Omit<Teaser, 'image' | 'link' | 'tracker'> & {
    promotionId?: string | number;
    link: string;
  };
}

interface CampaignBannerPromotion {
  source: TrackingPromotionCreative.Banner;
  campaign: {
    name: string;
    id: number;
    path: string;
    linkedContent?: string;
    destinationId?: string;
  };
}

interface BrandOutfitTeaserRecoPromotion {
  source: TrackingPromotionCreative.Teaser;
  outfit: Outfit;
}

interface APIPromotion {
  source: TrackingPromotionCreative.APIPromotion;
  apiPromotion: {
    promotionId: string;
    promotionName: string;
    creativeName: string;
    creativeSlot: string;
    locationId?: string;
    destination?: string;
  };
}

interface TeaserBannerPromotion {
  source: TrackingPromotionCreative.Banner;
  teaser: Teaser;
  sourceOverride: string;
}

interface HeroTileBannerPromotion {
  source: TrackingPromotionCreative.Banner;
  name: string;
  id?: string;
  ctaUrl?: string;
  external?: boolean;
  promotionTracker?: PromotionTracker;
}

export type BannerPromotion =
  | CampaignBannerPromotion
  | TeaserBannerPromotion
  | HeroTileBannerPromotion;

interface BasOspSubscriptionTilePromotion {
  source: TrackingPromotionCreative.BasOspSubscriptionTile;
  promotionId: string;
}

interface BasSovendusTilePromotion {
  source: TrackingPromotionCreative.Sovendus;
  promotionId: string;
}

export interface BrandRecoPromotion {
  source: TrackingPromotionCreative.BrandReco;
  brand: Brand;
}

export interface OutfitRecoPromotion {
  source: TrackingPromotionCreative.OutfitReco;
  outfit: Outfit;
}

export interface StoryRecoPromotion {
  source: TrackingPromotionCreative.StoryReco;
  story: Story & {promotionId?: string | number};
}

export interface HighlightPromotion {
  source: TrackingPromotionCreative.Highlight;
  highlightSlide: HighlightSlide;
  highlightId: number;
  promotionId: string;
  promotionName: string;
}

export interface TopBannerRecoPromotion {
  source: 'top_banner';
  banner: GetTopBannerResponse_TopBannerResponse;
  gender: 'male' | 'female' | 'kids';
}

export interface BasPromotionMonitoring {
  source: 'monitoring';
  originalSource:
    | TrackingPromotionCreative.BasCategoryHeader
    | TrackingPromotionCreative.BasCategoryTeaser
    | TrackingPromotionCreative.BasAdpBanner
    | TrackingPromotionCreative.BasBasketBanner
    | TrackingPromotionCreative.BasNavigationBanner
    | TrackingPromotionCreative.BasWishlistBanner
    | TrackingPromotionCreative.BasLogoIntegration
    | TrackingPromotionCreative.BasBrowseTopBanner
    | TrackingPromotionCreative.BasAddToBasketFlyOut;
  advertisement: {
    id: string;
    name: string;
  };
}

export interface RecoListTrackingData {
  sectionIndex: number;
  recoType?: string;
  headline?: string;
  elements?: TrackingRecoElement[];
}

export interface ProductTrackingData
  extends MarkOptional<RecoListTrackingData, 'sectionIndex'> {
  sortingChannel?: string;
}

export type ContentBlocksSourceType = 'cta' | 'banner' | 'video';

export interface CustomPagesPromotion {
  source: TrackingPromotionCreative.CustomPage;
  contentBlockSource: ContentBlocksSourceType;
  id: string;
  name: string;
  locationId: string;
  path: {
    url: string;
    external: boolean;
  };
}

export interface NewsCardPromotion {
  source: TrackingPromotionCreative.Newscard;
  id: string;
  name: string;
  locationId: string;
  path: string;
}

interface PromotionProps extends Omit<BaseTrackerProps, 'track'> {
  promotion: Promotion;
  recoList?: RecoListTrackingData;
  streamPosition: number;
  threshold?: number;
  triggerOnce?: boolean;
  pageType?: TrackingPageTypeWithId | TrackingPageTypeWithoutId;
  ['data-testid']?: string;
}

export function TrackPromotions({
  children,
  promotion,
  streamPosition,
  recoList,
  threshold = 0.5,
  triggerOnce,
  pageType,
  ...rest
}: PromotionProps) {
  const trackPromotion = usePromotionTracking({
    promotion,
    recoList,
    streamPosition,
    pageType,
  });

  return (
    <BaseTracker
      threshold={threshold}
      track={trackPromotion}
      triggerOnce={triggerOnce}
      {...rest}
    >
      {children}
    </BaseTracker>
  );
}

export const TrackPromotionStoryRecoNormalizer: FC<
  React.PropsWithChildren<
    {
      data: Record<string, any>;
      streamPosition: number;
    } & Omit<PromotionProps, 'promotion'>
  >
> = props => {
  const {
    data: {testId, isPremium, image, headline, subline, to},
    ...rest
  } = props;

  return (
    <TrackPromotions
      promotion={{
        story: {
          promotionId: testId,
          id: testId,
          path: to,
          gender: 0,
          contentType: 0,
          headline: {default: headline},
          subline: {default: subline},
          isPremium,
          isSale: false,
          isSponsored: false,
          images: {landscape: image, portrait: image},
        },
        source: TrackingPromotionCreative.StoryReco,
      }}
      {...rest}
    >
      {props.children}
    </TrackPromotions>
  );
};

export const TrackPromotionTeaserRecoNormalizer: FC<
  PropsWithChildren<{
    data: Record<string, any>;
    streamPosition: number;
    recoList?: RecoListTrackingData;
    trackingKey?: number;
  }>
> = ({streamPosition, data, recoList, trackingKey, children}) => {
  const {testId, headline, subline, to, teaserName, linkTargetId} = data;

  return (
    <TrackPromotions
      promotion={{
        teaser: {
          promotionId: testId,
          headline,
          subline,
          link: to,
          promotionTracking: {
            id: testId,
            name: teaserName,
            linkTargetId,
            linkTargetType: '',
          },
        },
        source: TrackingPromotionCreative.Teaser,
      }}
      streamPosition={streamPosition}
      recoList={recoList}
      trackingKey={trackingKey}
    >
      {children}
    </TrackPromotions>
  );
};

export function usePromotionTracking({
  promotion,
  recoList,
  streamPosition,
  pageType,
}: {
  promotion: Promotion;
  recoList?: RecoListTrackingData;
  streamPosition: number;
  pageType?: TrackingPageTypeWithId | TrackingPageTypeWithoutId;
}) {
  const {pageContextCallback} = useTracking();

  return useTrackingFactory(
    () =>
      getPromotionData({
        promotion,
        streamPosition,
        recoList,
        pageType: pageType || pageContextCallback().type,
      }),
    trackPromotionInteraction,
  );
}
